import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, AlertCircle, Calendar, Clock, CreditCard } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultationSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [consultation, setConsultation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setError('No session ID found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/consultation/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid' && data.consultation_confirmed) {
        setConsultation(data.consultation);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setError('Error verifying payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      <section className="pt-32 pb-16 flex items-center justify-center min-h-[60vh]">
        <div className="max-w-lg mx-auto px-4 sm:px-6 text-center">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-8 sm:p-12">
            
            {status === 'checking' && (
              <>
                <div className="w-24 h-24 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#8B5CF6]">
                  <Loader2 size={48} className="text-[#8B5CF6] animate-spin" />
                </div>
                
                <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-4">
                  Confirming Your Booking...
                </h1>
                
                <p className="text-[var(--text-muted)]">
                  Please wait while we verify your payment.
                </p>
              </>
            )}

            {status === 'success' && consultation && (
              <>
                <div className="w-24 h-24 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#10B981]">
                  <Check size={48} className="text-[#10B981]" />
                </div>
                
                <h1 className="text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-4">
                  Booking Confirmed!
                </h1>
                
                <p className="text-lg text-[var(--text-secondary)] mb-6">
                  Your {consultation.consultation_name} is scheduled.
                </p>
                
                <div className="bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl p-6 mb-8 text-left">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Calendar size={20} className="text-[#8B5CF6]" />
                      <div>
                        <p className="text-[var(--text-muted)] text-sm">Date</p>
                        <p className="text-[var(--text-primary)] font-semibold">{consultation.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-[#8B5CF6]" />
                      <div>
                        <p className="text-[var(--text-muted)] text-sm">Time</p>
                        <p className="text-[var(--text-primary)] font-semibold">{consultation.time}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <CreditCard size={20} className="text-[#10B981]" />
                      <div>
                        <p className="text-[var(--text-muted)] text-sm">Amount Paid</p>
                        <p className="text-[#10B981] font-semibold">${consultation.price}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-[var(--text-muted)] mb-8">
                  We've sent a confirmation email to <strong className="text-[var(--text-primary)]">{consultation.email}</strong> with meeting details.
                </p>
                
                <button
                  onClick={() => navigate('/')}
                  data-testid="back-to-home-btn"
                  className="px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' }}
                >
                  Back to Home
                </button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-24 h-24 rounded-full bg-[#EF4444]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#EF4444]">
                  <AlertCircle size={48} className="text-[#EF4444]" />
                </div>
                
                <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-4">
                  Something Went Wrong
                </h1>
                
                <p className="text-[var(--text-muted)] mb-8">
                  {error}
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/book-consultation')}
                    className="px-6 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' }}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-3 font-semibold text-[var(--text-secondary)] border border-[var(--border)] rounded-xl transition-all hover:border-[var(--border-hover)]"
                  >
                    Back to Home
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ConsultationSuccess;
